import {useFlags} from 'launchdarkly-react-client-sdk';
import {throttle} from 'lodash';
import React, {useEffect, useLayoutEffect, useRef, useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import {Button, Headline, Icon} from '@lightricks/react-design-system';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {type CreatorBrandSafetyVetting} from '@/types/creatorBrandSafetyVetting';
import {VettableCreators} from '@/types/vettableCreatorsResponse';
import useHandleToleranceSettingsModal from '@/views/vetting/components/brand-safety-tolerance-settings-modal/useHandleToleranceSettingsModal';
import Page from '@/components/page';
import useCreatorBrandSafetyVetting from '@/hooks/queries/use-creator-brand-safety-vettings-query';
import useSubscriptionQuery from '@/hooks/queries/use-subscription-query';
import useVettableCreatorsQuery from '@/hooks/queries/use-vettable-creators-query';
import useBrandId from '@/hooks/use-brand-id';
import {useDebounce} from '@/hooks/use-debounce';
import useGuardedFetchNextPage from '@/hooks/use-guarded-fetch-next-page';
import useVettingHubStore, {vettingHubActions} from '@/stores/vettingHubStore';
import styles from './VettingHub.module.scss';
import SearchSection from './components/search/search-section';
import VettingsTable from './components/vettings-table';
import trackBrandSafetyDetailEvent from './utils/vettingHubAnalytics';

type VettingHubProps = {
  tableData?: CreatorBrandSafetyVetting[];
};

const SEARCH_DEBOUNCE_MS = 300;

function VettingHub(props: VettingHubProps) {
  const flags = useFlags();
  const brandId = useBrandId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, sorting] = useVettingHubStore(
    useShallow((state) => [state.activeTab, state.sorting])
  );

  const [searchInputValue, setSearchInputValue] = React.useState(
    searchParams.get('search') ?? ''
  );
  const debouncedSearch = useDebounce(searchInputValue, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const current = prev.get('search') ?? '';
        if (debouncedSearch === current) return prev;
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set('search', debouncedSearch);
        } else {
          next.delete('search');
        }
        return next;
      },
      {replace: true}
    );
  }, [debouncedSearch]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInputValue(value);
  }, []);

  const {
    data: vettingsResponseData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    tabCounts,
    totalCount,
  } = useCreatorBrandSafetyVetting({
    brandId,
    tab: activeTab,
    sorting,
    search: debouncedSearch || undefined,
    scope: flags.fifaRevamp ? 'agency' : undefined,
  });

  const guardedFetchNextPage = useGuardedFetchNextPage(
    fetchNextPage,
    isFetching
  );

  const {open: openToleranceSettings} = useHandleToleranceSettingsModal();

  const pageScrollPosition = useVettingHubStore(
    useShallow((state) => state.pageScrollPosition)
  );

  const pageRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      if (pageRef.current && pageScrollPosition) {
        pageRef.current.scrollTo({top: pageScrollPosition || 0});
      }
    });
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  useEffect(() => {
    if (!pageRef.current) {
      return () => {};
    }

    const onScroll = throttle((event: React.UIEvent<HTMLElement>) => {
      const {scrollTop} = event.target as HTMLDivElement;
      vettingHubActions.setPageScrollPosition(scrollTop);
    }, 500) as unknown as EventListener;

    pageRef.current.addEventListener('scroll', onScroll);

    return () => {
      if (pageRef.current) {
        pageRef.current.removeEventListener('scroll', onScroll);
      }
    };
  }, [pageRef]);

  useEffect(() => {
    const flow = AnalyticsService.createFlow(FLOW_NAMES.BRAND_SAFETY_CREATOR);
    AnalyticsService.dispatchEvent(eventNames.BRAND_SAFETY_FLOW_STARTED, {
      flow_id: flow.flow_id,
      screen_presentation_id: flow.screen_presentation_id,
      brand_safety_screen_name: 'Hub',
      source: 'in-platform',
    });
  }, []);

  const {subscription} = useSubscriptionQuery({
    brandId,
  });

  const {tableData = vettingsResponseData} = props;

  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined
  );

  const {data: searchResults, isFetching: isSearching} =
    useVettableCreatorsQuery({searchTerm});

  useEffect(() => {
    if (!searchResults) return;
    const payload = {
      brand_safety_detail_name: 'Search Results',
      brand_safety_detail_value: JSON.stringify(searchResults),
    };
    trackBrandSafetyDetailEvent(payload);
  }, [JSON.stringify(searchResults)]);

  const onSubmitSearch = (value: string) => {
    trackBrandSafetyDetailEvent({
      brand_safety_detail_name: 'Submit Search',
      brand_safety_detail_value: value,
    });
    setSearchTerm(value);
  };

  const onCloseResults = () => {
    setSearchTerm(undefined);
  };

  return (
    <Page
      id="vetting-hub"
      className={styles.page}
      childrenClassName={styles.vettingHubContainer}
      testID="vetting-hub"
      pageRef={pageRef}
    >
      <SearchSection
        darkMode={subscription.isSafeCollab}
        searchResults={searchResults as VettableCreators}
        onSubmitSearch={onSubmitSearch}
        onCloseResults={onCloseResults}
        isLoading={isSearching}
      />
      <div className={styles.tableTitle}>
        <Headline size="md">{translate('my-vetting-list')}</Headline>
        <Button
          startIcon={
            <Icon
              appearance="neutral"
              size="medium"
              name="Features-Edit-Basic"
            />
          }
          mode="elevated"
          size="medium"
          appearance="neutral"
          onClick={openToleranceSettings}
        >
          {translate('Edit risk tolerance')}
        </Button>
      </div>
      <VettingsTable
        data={tableData ?? []}
        isLoading={isLoading ?? false}
        tabCounts={tabCounts}
        totalCount={totalCount}
        onEndReached={guardedFetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        searchValue={searchInputValue}
        onSearchChange={handleSearchChange}
      />
    </Page>
  );
}

export default VettingHub;
